// Los filtros para elegir a QUIÉN le mandas algo.
//
// Nacieron dentro de Correo Masivo y ahí se fueron afinando a base de fallos
// reales (ver los comentarios de cada regla abajo). Cuando Lissette pidió el
// mismo selector al inscribir gente en una Secuencia (2026-08-20), copiarlos
// habría sido garantizar que dentro de un mes una pantalla filtra bien y la
// otra no — porque los arreglos de estas reglas siempre se hacen en un solo
// sitio y nadie se acuerda del gemelo.
//
// Aquí vive SOLO la lógica, sin nada de pantalla, para poder ejecutarla en las
// pruebas sin montar React.

import { esEstatusCancelado } from './policyStatuses.js';
import { ageFromDob } from './utils.js';
import { contarEtiquetas, etiquetasDe, pasaElFiltroDeEtiqueta, SIN_ETIQUETA } from './filtroDeEtiquetas.js';

export const EMPTY_CLIENT_FILTERS = {
  search: '',
  // TAMBIÉN listas (Lissette 2026-08-30, al probar lo de arriba): *"pero el
  // filtro de estatus migratorio no me deja escoger varios"*. Tiene once
  // opciones — mandar un correo a los de DACA y los de TPS obligaba a
  // mandarlo dos veces, con el mismo problema de siempre: quien encaja en las
  // dos lo recibe dos veces.
  //
  // El idioma NO se toca: solo hay Español e Inglés, así que marcar los dos es
  // exactamente "cualquier idioma". Un selector de casillas ahí serían tres
  // clics para lo que ya hace uno.
  status: [],
  gender: [],
  immigration: [],
  lang: 'all',
  // LISTAS, no un valor suelto (Lissette 2026-08-30): *"en correos masivos
  // quisiera poder escoger varios, al igual que póliza, al igual que
  // compañía"*. Vacía = todas, que es la misma convención que ya usa
  // FiltroMultiple en Clientes y en Pólizas.
  policyType: [],
  carrier: [],
  // DÓNDE VIVE y QUIÉN LO LLEVA (Lissette 2026-09-11: *"en email masivo coloca
  // un filtro de por estado, y por agente asignado"*). Los dos ya existían en
  // Pólizas y faltaban aquí, que es donde se decide a quién se le escribe.
  //
  // Listas, como los de arriba: una campaña para Florida y Georgia es una sola
  // campaña, no dos — y mandarla dos veces se la manda dos veces a quien tenga
  // fichas en ambas.
  state: [],
  assignedAgent: [],
  // EL ESTATUS DE LA PÓLIZA (Lissette 2026-09-24: *"agregar a donde enviamos
  // correos masivos e email filtro de policy status"*). Lista, como los demás:
  // vacía = cualquier estatus. Ver `polizaCoincide` para cómo se combina con
  // el tipo de póliza y la compañía.
  policyStatus: [],
  // LA EDAD, entre dos números (Lissette 2026-10-15: *"créame un filtro en
  // correos masivos para poder filtrar entre edades"*). Texto, como sale del
  // campo: vacío = sin límite por ese lado. Ver `rangoDeEdad`.
  ageMin: '',
  ageMax: '',
  // LOS TAGS PERSONALIZADOS (Lissette 2026-10-23: *"en correos masivo
  // coloques los filtro de tag personalizados de cliente y de pólizas"*).
  // Listas, como los demás: vacía = cualquiera; 'none' (SIN_ETIQUETA) = "Sin
  // etiqueta". La comparación es la de filtroDeEtiquetas.js, sin distinguir
  // mayúsculas — la misma que Clientes, Pólizas, el Reporte de Ventas y los
  // paneles. El de póliza se cumple con UNA póliza (ver `polizaCoincide`).
  clientTag: [],
  policyTag: [],
};

/** "65" -> 65; vacío, letras o negativos -> null (ese lado sin límite). */
function edadDelCampo(v) {
  const s = String(v == null ? '' : v).trim();
  if (!/^\d{1,3}$/.test(s)) return null;
  return Number(s);
}

/**
 * El rango de edad pedido, o `null` si no hay ninguno.
 *
 * Si se escribe al revés ("desde 80 hasta 65") se lee como 65–80: lo que
 * quiere decir está claro, y dejar la lista vacía sin explicar por qué es
 * justo el fallo silencioso que esta pantalla no se puede permitir.
 */
export function rangoDeEdad(filtros) {
  const f = filtros || {};
  let desde = edadDelCampo(f.ageMin);
  let hasta = edadDelCampo(f.ageMax);
  if (desde === null && hasta === null) return null;
  if (desde !== null && hasta !== null && desde > hasta) [desde, hasta] = [hasta, desde];
  return { desde, hasta };
}

/**
 * ¿Cumple el rango? Ambos extremos CUENTAN ("de 18 a 26" incluye a quien tiene
 * 26). Quien no tiene fecha de nacimiento en la ficha NO entra: no se sabe su
 * edad. La pantalla dice cuántos se quedan fuera por eso
 * (`sinFechaDeNacimiento`), para que no desaparezcan sin explicación.
 */
export function edadDentroDelRango(c, rango, hoy = new Date()) {
  if (!rango) return true;
  const e = ageFromDob(c && c.dob, hoy);
  if (e === null) return false;
  if (rango.desde !== null && e < rango.desde) return false;
  if (rango.hasta !== null && e > rango.hasta) return false;
  return true;
}

/** El estado donde vive el cliente. Dos columnas por historia (`state` es la
 *  nueva, `addressState` la de las fichas viejas), así que se pregunta aquí y
 *  no en cada pantalla: con una copia por sitio, el día que aparezca una
 *  tercera forma habrá pantallas que filtren bien y pantallas que no. */
export const estadoDeCliente = (c) => String((c && (c.state || c.addressState)) || '').trim();

/** El agente que lleva a ese cliente. */
export const agenteDeCliente = (c) => String((c && c.assignedAgent) || '').trim();

/**
 * Las opciones de esos dos desplegables, sacadas de la cartera de verdad.
 *
 * No de un catálogo fijo: ofrecer estados o agentes que no tiene nadie llena la
 * lista de opciones que solo pueden dar cero resultados. Se agrupa sin mirar
 * mayúsculas —"FL" y "fl" son un estado, no dos— y se conserva la primera
 * grafía vista, que es la misma regla que ya usan las compañías.
 */
export function opcionesDeClientes(clients) {
  const estados = new Map();
  const agentes = new Map();
  for (const c of clients || []) {
    const e = estadoDeCliente(c);
    if (e && !estados.has(e.toLowerCase())) estados.set(e.toLowerCase(), e);
    const a = agenteDeCliente(c);
    if (a && !agentes.has(a.toLowerCase())) agentes.set(a.toLowerCase(), a);
  }
  const ordenar = (m) => Array.from(m.values()).sort((x, y) => x.localeCompare(y));
  return { allStates: ordenar(estados), allAgents: ordenar(agentes) };
}

/**
 * Un filtro de lista, venga como venga.
 *
 * Acepta la forma VIEJA —`'all'` o un valor suelto— además de la lista, porque
 * este objeto viaja en el estado de dos pantallas y no hay forma de garantizar
 * que nadie guarde uno con la forma anterior. Devuelve siempre una lista, y
 * vacía significa "todas".
 */
export function comoLista(v) {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (!v || v === 'all') return [];
  return [v];
}

/**
 * El MISMO estatus migratorio guardado con dos nombres distintos.
 *
 * 'asylum' es la forma vieja de 'asylee' y sigue viva en fichas antiguas y en
 * lo que entra por importación: el catálogo del backend acepta las dos a
 * propósito (`'asylee', 'asylum', // asylee = current form, asylum = legacy`).
 * El desplegable solo ofrece 'asylee', así que sin esto marcar
 * "Asilado / Refugiado" dejaba fuera a los asilados antiguos — y no habría
 * nada en pantalla que lo dijera.
 */
const MISMO_ESTATUS = { asylum: 'asylee' };
const estatusCanonico = (v) => MISMO_ESTATUS[v] || v;

/**
 * Índices por cliente que necesitan los filtros de póliza, más las listas de
 * opciones para pintar los desplegables. Se calcula una vez sobre las pólizas.
 */
export function buildPolicyIndexes(policies) {
  const byType = new Map();
  const byCarrier = new Map();
  // Las pólizas de cada cliente, una a una. El filtro de ESTATUS las necesita
  // así (ver `polizaCoincide`): con los índices sueltos de tipo y compañía no
  // se puede saber si la póliza activa es la de Salud o la de Vida.
  const policiesByClient = new Map();
  const tipos = new Set();
  // Las compañías son texto libre en la póliza, así que "Ambetter" y
  // "AMBETTER" son UNA compañía, no dos opciones del desplegable. Se compara
  // en minúsculas y se enseña la primera forma que apareció.
  const carrierPorMinuscula = new Map();

  (policies || []).forEach((p) => {
    if (p && p.type) tipos.add(p.type);
    const carrier = String((p && p.carrier) || '').trim();
    if (carrier && !carrierPorMinuscula.has(carrier.toLowerCase())) {
      carrierPorMinuscula.set(carrier.toLowerCase(), carrier);
    }
    if (!p || !p.clientId) return;
    if (!policiesByClient.has(p.clientId)) policiesByClient.set(p.clientId, []);
    policiesByClient.get(p.clientId).push({
      type: p.type || '',
      carrier: carrier.toLowerCase(),
      status: p.status || 'active',
      // Ya limpias (sin blancos): el filtro de tag de póliza mira la MISMA
      // póliza que el de tipo, compañía y estatus.
      tags: etiquetasDe(p),
    });
    if (p.type) {
      if (!byType.has(p.clientId)) byType.set(p.clientId, new Set());
      byType.get(p.clientId).add(p.type);
    }
    if (carrier) {
      if (!byCarrier.has(p.clientId)) byCarrier.set(p.clientId, new Set());
      byCarrier.get(p.clientId).add(carrier.toLowerCase());
    }
  });

  return {
    byType,
    byCarrier,
    policiesByClient,
    allTypes: Array.from(tipos).sort(),
    allCarriers: Array.from(carrierPorMinuscula.values()).sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * ¿Esta póliza tiene el estatus pedido?
 *
 * "Cancelada" no es solo el id `cancelled`: también cuentan los estatus
 * PROPIOS de la agencia que se llaman "Cancelada", "Vencida"... y los ids
 * viejos ('lapsed', 'expired', 'inactive'). Es la misma regla que usa la
 * pestaña Canceladas de Pólizas (`esEstatusCancelado`); con otra aquí, el
 * correo de "reactiva tu póliza" dejaría fuera justo a esos clientes.
 * Una póliza sin estatus guardado es activa, como en el resto de la app.
 */
export function estatusDePolizaCoincide(estatus, pedido) {
  const s = String(estatus || 'active');
  if (pedido === 'cancelled') return esEstatusCancelado(s);
  // "Activa" cuenta IGUAL que la pestaña Activas de Pólizas y que Pagos del
  // Mes: todo lo que no está cancelado ni pendiente — Gracia y los estatus
  // PROPIOS de la agencia ("SOMETIDA"...) incluidos. Mirando solo el id
  // 'active', la agencia de Denisse veía 100 y pico clientes en el masivo
  // contra 553 pólizas activas en Pólizas (Lissette 2026-10-14).
  if (pedido === 'active') return !esEstatusCancelado(s) && s !== 'pending';
  return s === pedido;
}

/**
 * ¿Tiene este cliente UNA póliza que cumpla a la vez estatus, tipo y compañía?
 *
 * La MISMA póliza, no tres distintas. Quien pide "Salud" + "Activa" quiere a
 * los que tienen la de Salud activa; mirando cada filtro por separado entraría
 * también quien tiene la de Salud cancelada y la de Vida activa — y a ese
 * cliente le llegaría un correo sobre una póliza que ya no tiene.
 */
function polizaCoincide(polizas, estatusPedidos, tiposPedidos, carriersPedidos, etiquetasPedidas = []) {
  const carriers = carriersPedidos.map((x) => String(x).toLowerCase());
  return (polizas || []).some((p) =>
    (!estatusPedidos.length || estatusPedidos.some((e) => estatusDePolizaCoincide(p.status, e)))
    && (!tiposPedidos.length || tiposPedidos.includes(p.type))
    && (!carriers.length || carriers.includes(p.carrier))
    // El TAG DE PÓLIZA también es de ESA póliza: "Salud" + tag "Renovar" es
    // quien tiene la de Salud marcada para renovar, no quien tiene una de
    // Salud y otra de Vida con el tag. "Sin etiqueta" = una póliza sin tags.
    && (!etiquetasPedidas.length || etiquetasPedidas.some((v) => pasaElFiltroDeEtiqueta(p, v))));
}

/**
 * Aplica los filtros a la lista de clientes.
 *
 * `requireEmail` deja fuera a quien no tiene correo: para mandar un correo o
 * inscribir en una secuencia no sirve de nada, y verlo en la lista solo lleva
 * a marcarlo y que luego "no le llegó".
 */
export function filterClients(clients, filtros, idx, { requireEmail = true, hoy = new Date() } = {}) {
  const f = { ...EMPTY_CLIENT_FILTERS, ...(filtros || {}) };
  const q = String(f.search || '').trim().toLowerCase();
  const indices = idx || { byType: new Map(), byCarrier: new Map() };
  const rango = rangoDeEdad(f);

  return (clients || []).filter((c) => {
    if (!c) return false;
    if (requireEmail && !c.email) return false;

    // El estado vuelve del API en MAYÚSCULAS (ACTIVE | INACTIVE | PROSPECT).
    // Se compara sin distinguir may/min porque antes los valores en minúscula
    // del desplegable no casaban nunca y el filtro no hacía nada, en silencio.
    const estados = comoLista(f.status);
    if (estados.length) {
      const s = String(c.status || '').toUpperCase();
      if (!estados.some((x) => String(x).toUpperCase() === s)) return false;
    }

    // El sexo se guarda como 'Male' / 'Female'. Vacío = nunca se preguntó, que
    // NO es lo mismo que "ninguno de los dos" — por eso 'none' es una opción
    // más y no la ausencia de filtro.
    const sexos = comoLista(f.gender);
    if (sexos.length) {
      const g = String(c.gender || '').trim().toLowerCase();
      if (!sexos.some((x) => (x === 'none' ? !g : g === x))) return false;
    }

    const migratorios = comoLista(f.immigration);
    if (migratorios.length) {
      const im = estatusCanonico(String(c.immigrationStatus || '').trim().toLowerCase());
      if (!migratorios.some((x) => (x === 'none' ? !im : im === estatusCanonico(x)))) return false;
    }

    if (f.lang !== 'all') {
      // NO hay opción "sin idioma": el mapeo de api.js rellena 'en' cuando la
      // ficha no lo trae, así que una ficha sin idioma es indistinguible de una
      // que dijo inglés. Ofrecer esa opción sería un desplegable que nunca
      // encuentra a nadie.
      if (String(c.preferredLanguage || 'en').trim().toLowerCase() !== f.lang) return false;
    }

    // Varias a la vez se leen como "O": quien tenga CUALQUIERA de las
    // elegidas entra. Exigir todas dejaría casi siempre la lista vacía — un
    // cliente rara vez tiene póliza de los tres ramos que marcaste.
    const tiposPedidos = comoLista(f.policyType);
    if (tiposPedidos.length) {
      const tipos = indices.byType.get(c.id);
      if (!tipos || !tiposPedidos.some((t) => tipos.has(t))) return false;
    }

    const carriersPedidos = comoLista(f.carrier);
    if (carriersPedidos.length) {
      const carriers = indices.byCarrier.get(c.id);
      if (!carriers) return false;
      if (!carriersPedidos.some((x) => carriers.has(String(x).toLowerCase()))) return false;
    }

    const estatusPedidos = comoLista(f.policyStatus);
    const etiquetasDePoliza = comoLista(f.policyTag);
    if (estatusPedidos.length || etiquetasDePoliza.length) {
      const polizas = indices.policiesByClient && indices.policiesByClient.get(c.id);
      if (!polizaCoincide(polizas, estatusPedidos, tiposPedidos, carriersPedidos, etiquetasDePoliza)) return false;
    }

    // Tag del CLIENTE: entra quien tenga CUALQUIERA de los marcados (o
    // ninguno, con "Sin etiqueta").
    const etiquetasDeCliente = comoLista(f.clientTag);
    if (etiquetasDeCliente.length && !etiquetasDeCliente.some((v) => pasaElFiltroDeEtiqueta(c, v))) return false;

    // El estado y el agente se comparan sin mirar mayúsculas por lo mismo que
    // las compañías: en las fichas conviven "FL" y "fl".
    const estadosPedidos = comoLista(f.state);
    if (estadosPedidos.length) {
      const e = estadoDeCliente(c).toLowerCase();
      if (!estadosPedidos.some((x) => (x === 'none' ? !e : String(x).trim().toLowerCase() === e))) return false;
    }

    const agentesPedidos = comoLista(f.assignedAgent);
    if (agentesPedidos.length) {
      const a = agenteDeCliente(c).toLowerCase();
      if (!agentesPedidos.some((x) => (x === 'none' ? !a : String(x).trim().toLowerCase() === a))) return false;
    }

    if (!edadDentroDelRango(c, rango, hoy)) return false;

    if (q) {
      const hay = `${c.firstName || ''} ${c.lastName || ''} ${c.email || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/**
 * Cuántas PÓLIZAS hay detrás de los clientes que deja el filtro.
 *
 * Lissette, 2026-10-14: Pólizas decía "553 de Salud activas" y Correo Masivo
 * "549" con los mismos filtros, y no se entendía por qué. El masivo cuenta
 * personas —un cliente con dos pólizas recibe UN correo—, así que la pantalla
 * enseña los dos números juntos. Este es el de pólizas: las de esos clientes
 * que cumplen tipo, compañía y estatus (la misma regla de `polizaCoincide`).
 *
 * Se cuenta sobre los clientes SIN exigir correo, para que cuadre con Pólizas:
 * una póliza de alguien sin correo sigue siendo una póliza.
 * `null` cuando no hay ningún filtro de póliza puesto: entonces no hay nada
 * que comparar.
 */
export function polizasQueCoinciden(clients, filtros, idx) {
  const f = { ...EMPTY_CLIENT_FILTERS, ...(filtros || {}) };
  const tipos = comoLista(f.policyType);
  const carriers = comoLista(f.carrier).map((x) => String(x).toLowerCase());
  const estatus = comoLista(f.policyStatus);
  const etiquetas = comoLista(f.policyTag);
  if (!tipos.length && !carriers.length && !estatus.length && !etiquetas.length) return null;
  const porCliente = (idx && idx.policiesByClient) || new Map();
  let n = 0;
  for (const c of filterClients(clients, f, idx, { requireEmail: false })) {
    for (const p of porCliente.get(c.id) || []) {
      if (tipos.length && !tipos.includes(p.type)) continue;
      if (carriers.length && !carriers.includes(p.carrier)) continue;
      if (estatus.length && !estatus.some((e) => estatusDePolizaCoincide(p.status, e))) continue;
      if (etiquetas.length && !etiquetas.some((v) => pasaElFiltroDeEtiqueta(p, v))) continue;
      n += 1;
    }
  }
  return n;
}

/**
 * Las opciones de los dos filtros de TAG, sacadas de la cartera de verdad
 * (Lissette 2026-10-23). Igual que estados y agentes: no del catálogo de la
 * agencia, porque un tag que no lleva nadie solo puede dar cero.
 *
 * Devuelve, para cada filtro, `valores` (lo que se marca; 'none' al final si
 * hay alguien sin tag) y `cuenta` (valor -> cuántos CLIENTES salen al
 * marcarlo). Se cuentan personas y no pólizas porque eso es lo que esta
 * pantalla manda: un cliente con tres pólizas "Renovar" recibe UN correo.
 *
 * Con `requireEmail` (por defecto, como `filterClients`) solo cuentan los que
 * tienen correo: el número del desplegable tiene que ser el que luego sale.
 */
export function opcionesDeEtiquetas(clients, policies, { requireEmail = true } = {}) {
  const clientes = (clients || []).filter((c) => c && c.id && (!requireEmail || c.email));
  const ids = new Set(clientes.map((c) => c.id));

  // Las pólizas de cada cliente que está en la lista.
  const porCliente = new Map();
  for (const p of policies || []) {
    if (!p || !ids.has(p.clientId)) continue;
    if (!porCliente.has(p.clientId)) porCliente.set(p.clientId, []);
    porCliente.get(p.clientId).push(etiquetasDe(p));
  }
  // Por cliente: la unión de los tags de sus pólizas (para contar personas,
  // no pólizas), y si tiene alguna póliza sin tag (para "Sin etiqueta").
  const unionDeTags = [];
  let conPolizaSinTag = 0;
  for (const listas of porCliente.values()) {
    unionDeTags.push({ tags: listas.flat() });
    if (listas.some((l) => l.length === 0)) conPolizaSinTag += 1;
  }

  const armar = (cuenta, sinEtiqueta) => {
    const valores = cuenta.opciones.map((o) => o.label);
    const n = new Map(cuenta.opciones.map((o) => [o.label, o.n]));
    if (sinEtiqueta) { valores.push(SIN_ETIQUETA); n.set(SIN_ETIQUETA, sinEtiqueta); }
    return { valores, cuenta: n };
  };
  const deClientes = contarEtiquetas(clientes);
  return {
    clientTags: armar(deClientes, deClientes.sinEtiqueta),
    policyTags: armar(contarEtiquetas(unionDeTags.filter((x) => x.tags.length)), conPolizaSinTag),
  };
}

/**
 * Cuántos se quedan fuera del filtro de edad SOLO por no tener fecha de
 * nacimiento: los que con el resto de filtros sí saldrían. 0 si no hay rango.
 */
export function sinFechaDeNacimiento(clients, filtros, idx, opciones = {}) {
  const f = { ...EMPTY_CLIENT_FILTERS, ...(filtros || {}) };
  if (!rangoDeEdad(f)) return 0;
  const sinEdad = { ...f, ageMin: '', ageMax: '' };
  const hoy = opciones.hoy || new Date();
  return filterClients(clients, sinEdad, idx, opciones)
    .filter((c) => ageFromDob(c.dob, hoy) === null).length;
}

/** ¿Hay algún filtro puesto? Para poder ofrecer "Quitar filtros". */
export function hasActiveFilters(filtros) {
  const f = { ...EMPTY_CLIENT_FILTERS, ...(filtros || {}) };
  return Object.keys(EMPTY_CLIENT_FILTERS).some((k) => {
    // Los de lista se miran por CONTENIDO. Comparar el array con `!==` daría
    // siempre true —dos arrays vacíos nunca son el mismo objeto— y entonces
    // "Quitar filtros" saldría encendido con la pantalla recién abierta.
    if (Array.isArray(EMPTY_CLIENT_FILTERS[k])) return comoLista(f[k]).length > 0;
    return f[k] !== EMPTY_CLIENT_FILTERS[k];
  });
}

/**
 * De los que están MARCADOS, cuáles NO salen con el filtro de ahora mismo.
 *
 * Existe por el reporte de Lissette (2026-09-10, Correo Masivo): *"no está
 * funcionando bien el filtro; a pesar de que se le dice solo activos, o de que
 * pongo otra compañía, vuelve a aparecer"*.
 *
 * El filtro SÍ funcionaba: lo que no seguía al filtro era LA SELECCIÓN. Se
 * marcaban 40 de Florida Blue, se cambiaba el filtro a otra compañía, y esos 40
 * seguían dentro — fuera de la lista, invisibles, y aun así se les mandaba el
 * correo. Un destinatario que no se ve en ninguna parte y que igual recibe es
 * lo más caro que puede fallar en esta pantalla.
 *
 * Se devuelve la LISTA y no un simple sí/no porque la pantalla necesita decir
 * CUÁNTOS son: "hay 12 que no coinciden" es un aviso accionable; "hay algunos"
 * no lo es.
 *
 * `filtrados` son los clientes que el filtro deja pasar ahora mismo.
 */
export function seleccionadosFueraDelFiltro(idsMarcados, filtrados) {
  const dentro = new Set();
  for (const c of filtrados || []) { if (c && c.id) dentro.add(c.id); }
  const out = [];
  for (const id of idsMarcados || []) { if (id && !dentro.has(id)) out.push(id); }
  return out;
}
